import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { 
  GraduationCap, 
  Flame, 
  Clock, 
  ChevronRight,
  X,
  Trophy,
  Sparkles
} from "lucide-react";

export const LearningReminderModal = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reminderData, setReminderData] = useState(null);

  const checkReminder = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await api.get("/user/learning-reminder");
      const data = response.data;
      
      if (data.show_reminder && data.next_lesson) {
        setReminderData(data);
        setOpen(true);
      }
    } catch (error) {
      // Silently fail - reminders are optional
      console.log("Reminder check skipped");
    }
  }, [api, user]);

  useEffect(() => {
    // Check for reminder after a short delay when user logs in
    const timer = setTimeout(checkReminder, 2000);
    return () => clearTimeout(timer);
  }, [checkReminder]);

  const handleDismiss = async () => {
    try {
      await api.post("/user/dismiss-reminder");
    } catch (e) {
      // Ignore errors
    }
    setOpen(false);
  };

  const handleStartLesson = async () => {
    await handleDismiss();
    if (reminderData?.next_lesson?.course_id) {
      navigate(`/courses/${reminderData.next_lesson.course_id}`);
    }
  };

  if (!reminderData) return null;

  const { next_lesson, progress, streak, all_complete } = reminderData;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <GraduationCap className="w-6 h-6 text-primary" />
            Daily Learning Reminder
          </DialogTitle>
          <DialogDescription>
            Keep building your financial knowledge!
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Streak Display */}
          {streak?.current > 0 && (
            <div className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800">
              <Flame className="w-6 h-6 text-orange-500" />
              <span className="font-bold text-lg text-orange-600 dark:text-orange-400">
                {streak.current} day streak!
              </span>
              <Sparkles className="w-5 h-5 text-amber-500" />
            </div>
          )}

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Overall Progress</span>
              <span className="font-medium">{progress.completed}/{progress.total} lessons</span>
            </div>
            <div className="h-2.5 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary rounded-full transition-all duration-500"
                style={{ width: `${progress.percentage}%` }}
              />
            </div>
          </div>

          {/* Next Lesson Card */}
          {next_lesson && (
            <div 
              className="p-4 rounded-xl border border-border/50 bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer"
              onClick={handleStartLesson}
            >
              <p className="text-xs text-muted-foreground mb-1">
                Continue from: {next_lesson.course_title}
              </p>
              <h4 className="font-semibold mb-2">{next_lesson.title}</h4>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  {next_lesson.duration}
                </span>
              </div>
            </div>
          )}

          {/* All Complete State */}
          {all_complete && (
            <div className="text-center py-4">
              <Trophy className="w-12 h-12 mx-auto text-amber-500 mb-2" />
              <h4 className="font-semibold">Congratulations!</h4>
              <p className="text-sm text-muted-foreground">
                You&apos;ve completed all available courses!
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={handleDismiss}
          >
            <X className="w-4 h-4 mr-2" />
            Remind Me Later
          </Button>
          {next_lesson && (
            <Button 
              className="flex-1 gap-2"
              onClick={handleStartLesson}
            >
              Start Lesson
              <ChevronRight className="w-4 h-4" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
